import React, { useMemo, useEffect, useState } from "react";
import "../ganttwidget.css";
import { format, addDays, differenceInDays, startOfWeek, endOfWeek, addWeeks, differenceInWeeks, } from "date-fns";
import { Switch, Typography, Tooltip } from "@mui/material";
import { useParams } from 'react-router-dom';
import axios from "axios";

const GanttFreeTool = () => {
    const [tasks, setTasks] = useState([]);
    const [hoveredTask, setHoveredTask] = useState(null);
    const [timeline, setTimeline] = useState({ topRow: [], bottomRow: [] });
    const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
    const [taskPositions, setTaskPositions] = useState([]);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [containerHeight, setContainerHeight] = useState(0); // Dinamik yükseklik
    const calculateCellWidth = () => (viewMode === 'daily' ? 80 : 140);

    // Get code from URL parameters
    const { code } = useParams();

    const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
    });

    useEffect(() => {
        const fetchTasks = async () => {
            if (!code) {
                console.error("No code provided in URL");
                setIsLoading(false);
                return;
            }

            try {
                const response = await api.get(`/api/widget/onsite/${code}`);
                setTasks(response.data.tasks);
            } catch (error) {
                console.error("Error fetching tasks:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTasks();
    }, [code]);

    useEffect(() => {
        if (tasks.length > 0) {
            const taskHeight = tasks.length * 33; // Her task için 33px yükseklik
            const timelineHeight = 50; // Timeline için sabit bir yükseklik
            let totalHeight = taskHeight + timelineHeight; // Toplam yükseklik hesaplama

            if (viewMode === 'weekly') {
                totalHeight += 10; // Weekly görünüm için ek yükseklik
            }
            else {
                totalHeight -= 20;
            }

            setContainerHeight(totalHeight); // Toplam yüksekliği ayarla
            console.log('Calculated height based on tasks and view mode:', totalHeight);
        }
    }, [tasks, viewMode]); // Tasks veya viewMode değiştiğinde yeniden hesapla


  


    // Minimum başlangıç tarihi
    const minDate = useMemo(() => {
        if (tasks.length === 0) return null;
        const minDate = new Date(Math.min(...tasks.map((task) => new Date(task.start_date))));
        return minDate;
    }, [tasks]);

    // Maksimum bitiş tarihi
    const maxDate = useMemo(() => {
        if (tasks.length === 0) return null;
        const maxDate = new Date(Math.max(...tasks.map((task) => new Date(task.due_date))));
        return maxDate;
    }, [tasks]);

    useEffect(() => {
        calculateTimeline();
        calculateTaskPositions();
    }, [tasks, viewMode]);

    const calculateTimeline = () => {
        if (!minDate || !maxDate) {
            setTimeline({ topRow: [], bottomRow: [] });
            return;
        }

        const timelineArray = { topRow: [], bottomRow: [] };

        if (viewMode === 'daily') {
            let currentDate = minDate;
            while (currentDate <= maxDate) {
                timelineArray.topRow.push(format(currentDate, 'MMM d'));
                currentDate = addDays(currentDate, 1);
            }
        } else if (viewMode === 'weekly') {
            const startWeek = startOfWeek(minDate, { weekStartsOn: 1 });
            const endWeek = endOfWeek(maxDate, { weekStartsOn: 1 });

            let currentWeek = startWeek;
            while (currentWeek <= endWeek) {
                const start = format(currentWeek, 'MMM d');
                const end = format(addDays(currentWeek, 6), 'MMM d');
                timelineArray.topRow.push(`${start} - ${end}`);
                timelineArray.bottomRow.push(['M', 'T', 'W', 'T', 'F', 'S', 'S']);
                currentWeek = addWeeks(currentWeek, 1);
            }
        }

        setTimeline(timelineArray);
    };

    const calculateTaskPositions = () => {
        const sortedTasks = [...tasks].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        const positions = sortedTasks.map((task, index) => ({ ...task, top: index * 33 }));
        setTaskPositions(positions);
    };

    const getBarStyle = (start_date, due_date, status, color, top) => {
        const left =
            viewMode === 'daily'
                ? differenceInDays(new Date(start_date), minDate)
                : differenceInWeeks(
                    startOfWeek(new Date(start_date), { weekStartsOn: 1 }),
                    startOfWeek(minDate, { weekStartsOn: 1 })
                ) +
                (differenceInDays(new Date(start_date), startOfWeek(new Date(start_date), { weekStartsOn: 1 })) / 7); // Haftanın hangi gününden başladığını ekliyoruz

        const width =
            viewMode === 'daily'
                ? differenceInDays(new Date(due_date), new Date(start_date)) + 1
                : (differenceInDays(new Date(due_date), new Date(start_date)) + 1) / 7;

        return {
            left: `${left * calculateCellWidth()}px`,
            width: `${width * calculateCellWidth()}px`,
            top: `${top}px`,
            background: color, // Rastgele atanan rengi kullan,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        };
    };

    const handleViewModeChange = () => {
        setViewMode((prevMode) => (prevMode === 'daily' ? 'weekly' : 'daily'));
    };

    const renderGridLines = () => {
        const totalCells = timeline.topRow.length; // Haftalık hücre sayısı
        const cellWidth = calculateCellWidth(); // Haftalık hücre genişliği
        const dailyWidth = cellWidth / 7; // Günlük çizgi genişliği

        const gridLines = [];

        for (let i = 0; i < totalCells; i++) {
            // Haftalık çizgiler
            gridLines.push(
                <div
                    key={`week-${i}`}
                    className="grid-line-w"
                    style={{
                        left: `${i * cellWidth}px`,
                        width: '1px',
                        height: '100%',
                        position: 'absolute',
                        backgroundColor: '#ddd', // Haftalık çizgi rengi
                        zIndex: 0,
                    }}
                />
            );

            // Günlük çizgiler (sadece görevler ve grid alanı için)
            if (viewMode === 'weekly') {
                for (let j = 1; j < 7; j++) {
                    gridLines.push(
                        <div
                            key={`day-${i}-${j}`}
                            className="grid-line-w daily"
                            style={{
                                left: `${i * cellWidth + j * dailyWidth}px`,
                                width: '1px',
                                height: 'calc(100% - 30px)', // Top Row'u hariç tut
                                position: 'absolute',
                                top: '30px', // Çizgiler, top row'un altından başlasın
                                backgroundColor: '#eee',
                                zIndex: 0,
                            }}
                        />
                    );
                }
            }
        }

        return gridLines;
    };

   
    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!tasks || tasks.length === 0) {
        return <div>No tasks found for this Gantt Chart.</div>;
    }

    return (
        <div className="gantt-widget" style={{top:"20px"}}>

            <div className="view-switch-w" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {/* View Mode Switch */}
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '7px' }}>
                    <Typography variant="subtitle1">Daily</Typography>
                    <Switch checked={viewMode === 'weekly'} onChange={handleViewModeChange} />
                    <Typography variant="subtitle1">Weekly</Typography>
                </div>

                {/* Powered by GanttMaster */}
                <a
                    href="https://www.ganttmaster.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'inherit',
                        marginLeft: '20px',
                        marginRight: '5px'
                    }}
                >
                    <span style={{ color: '#FFFFFF', marginRight: '5px' }}>Powered by</span>
                    <span style={{ color: '#FFFFFF' }}>GANTT</span>
                    <span style={{ color: '#807efe', marginLeft: '2px' }}>MASTER</span>
                </a>
            </div>

            <div className="timeline-tasks-container-w" style={{ position: 'relative', height: `${containerHeight}px` }}>
                {/* Grid Lines */}
                <div className="grid-lines-w">{renderGridLines()}</div>

                {/* Timeline */}
                <div className="timeline-w">
                    <div className="top-row-w">
                        {timeline.topRow?.map((date, index) => (
                            <div
                                key={index}
                                className="timeline-cell-w"
                                style={{ width: `${calculateCellWidth()}px` }}
                            >
                                {date}
                            </div>
                        ))}
                    </div>

                    {viewMode === 'weekly' && (
                        <div className="bottom-row-w">
                            {timeline.bottomRow?.map((days, index) =>
                                days?.map((day, i) => (
                                    <div key={`${index}-${i}`} className="timeline-cell-w">
                                        {day}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Tasks */}
                <div className="tasks-w">
                    {taskPositions.map((task) => (
                        <Tooltip
                            key={task.id}
                            title={
                                <div>
                                    <Typography variant="subtitle2">{task.title}</Typography>
                                  
                                </div>
                            }
                            arrow
                            placement="top"
                            onOpen={() => setHoveredTask(task.id)}
                            onClose={() => setHoveredTask(null)}
                        >
                            <div
                                className="task-bar-w"
                                style={getBarStyle(task.start_date, task.due_date, task.status, task.color, task.top)}
                            >
                                {task.title}
                            </div>
                        </Tooltip>
                    ))}
                </div>
            </div>


        </div>
    );
};

export default GanttFreeTool;
